/**
 * Управление списком «отключённых звука» чатов (mutedChatIds).
 * Хранится в localStorage. При изменении генерирует событие `chatMuteChange`,
 * чтобы компоненты могли реагировать без перезагрузки.
 */

import { useState, useEffect } from 'react';

const STORAGE_KEY = 'hrnb_mutedChatIds';
const EVENT_NAME = 'chatMuteChange';

/**
 * @returns {Set<string>} — множество ID отключённых чатов
 */
export function getMutedChatIds() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? new Set(arr) : new Set();
    } catch {
        return new Set();
    }
}

/**
 * @param {string} chatId
 * @returns {boolean}
 */
export function isChatMuted(chatId) {
    if (!chatId) return false;
    return getMutedChatIds().has(String(chatId));
}

function persist(set) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
    } catch {
        /* квота / приватный режим — игнорируем */
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { chatId: null } }));
}

/**
 * Отключить звук для чата.
 * @param {string} chatId
 */
export function muteChat(chatId) {
    if (!chatId) return;
    const set = getMutedChatIds();
    set.add(String(chatId));
    persist(set);
}

/**
 * Включить звук для чата.
 * @param {string} chatId
 */
export function unmuteChat(chatId) {
    if (!chatId) return;
    const set = getMutedChatIds();
    set.delete(String(chatId));
    persist(set);
}

/**
 * Переключить mute для чата.
 * @param {string} chatId
 * @returns {boolean} — новое состояние (true = muted)
 */
export function toggleChatMute(chatId) {
    if (!chatId) return false;
    if (isChatMuted(chatId)) {
        unmuteChat(chatId);
        return false;
    }
    muteChat(chatId);
    return true;
}

/**
 * React-хук: подписка на изменения списка muted-чатов.
 * Возвращает текущий Set ID.
 *
 * @returns {Set<string>}
 */
export function useMutedChatIds() {
    const [muted, setMuted] = useState(() => getMutedChatIds());

    useEffect(() => {
        const handler = () => setMuted(getMutedChatIds());
        window.addEventListener(EVENT_NAME, handler);
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener(EVENT_NAME, handler);
            window.removeEventListener('storage', handler);
        };
    }, []);

    return muted;
}
